/**
 * Service AI - Couche métier pour les fonctionnalités d'IA
 * Délègue les appels HTTP au client API et ajoute de la logique métier
 */

import { aiClient } from '../api'
import type {
  QuizSchema,
  ClozeQuizSchema,
  SummarySchema,
  SupportedLanguage,
  AIHealthResponse,
  GradeResult,
} from '@sphere/ai/domain'
// Note: GenerateExerciseRequest et GenerateExerciseResponse sont spécifiques au frontend
import type { GenerateExerciseRequest } from '../api';
import type { GenerateExerciseResponse } from '../types/ai.dto';

const DEFAULT_LANGUAGE = 'fr' as SupportedLanguage

// ============================================================================
// Error Handling Helper
// ============================================================================

function handleAIError(error: any): string {
  if (error.response) {
    const status = error.response.status
    const data = error.response.data

    switch (status) {
      case 400:
        return data?.message || 'Fichier invalide ou paramètres incorrects'
      case 413:
        return 'Le fichier est trop volumineux'
      case 415:
        return 'Type de fichier non supporté'
      case 422:
        return data?.message || 'Le contenu du fichier ne peut pas être traité'
      case 500:
        return 'Erreur serveur lors de la génération'
      case 503:
        return 'Service IA temporairement indisponible'
      default:
        return data?.message || 'Une erreur est survenue lors de la génération'
    }
  }

  if (error.request) {
    return 'Impossible de contacter le service IA'
  }

  return error.message || 'Erreur inconnue'
}

// ============================================================================
// Quiz Generation
// ============================================================================

export async function generateQuiz(
  file: File,
  lang: SupportedLanguage = DEFAULT_LANGUAGE
): Promise<QuizSchema> {
  try {
    const response = await aiClient.quiz.generate(file, lang)
    return response.data
  } catch (error) {
    throw new Error(handleAIError(error))
  }
}

export async function generateClozeQuiz(
  file: File,
  lang: SupportedLanguage = DEFAULT_LANGUAGE
): Promise<ClozeQuizSchema> {
  try {
    const response = await aiClient.quiz.generateCloze(file, lang)
    return response.data
  } catch (error) {
    throw new Error(handleAIError(error))
  }
}

// ============================================================================
// Summary Generation
// ============================================================================

export async function generateSummary(
  file: File,
  lang: SupportedLanguage = DEFAULT_LANGUAGE
): Promise<SummarySchema> {
  try {
    const response = await aiClient.summary.generate(file, lang)
    return response.data
  } catch (error) {
    throw new Error(handleAIError(error))
  }
}

// ============================================================================
// PDF Generation
// ============================================================================

export async function generateExercisePdf(
  dto: GenerateExerciseRequest
): Promise<GenerateExerciseResponse> {
  try {
    const response = await aiClient.pdf.generateExercise(dto)
    const blob = new Blob([response.data], { type: 'application/pdf' })

    // Extraire le nom du fichier depuis les headers ou générer un nom par défaut
    const contentDisposition = response.headers['content-disposition']
    let filename = 'exercices.pdf'

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1]
      }
    }

    return { blob, filename }
  } catch (error) {
    throw new Error(handleAIError(error))
  }
}

export async function generateQAPdf(
  file: File,
  lang: SupportedLanguage = DEFAULT_LANGUAGE
): Promise<Blob> {
  try {
    const response = await aiClient.pdf.generateQA(file, lang)
    return new Blob([response.data], { type: 'application/pdf' })
  } catch (error) {
    throw new Error(handleAIError(error))
  }
}

// ============================================================================
// Grade Copy
// ============================================================================

export async function gradeCopy(
  answerKey: File,
  studentCopy: File,
  totalPoints: number,
  lang: SupportedLanguage = DEFAULT_LANGUAGE
): Promise<GradeResult> {
  try {
    const response = await aiClient.grade.gradeCopy(
      answerKey,
      studentCopy,
      totalPoints,
      lang
    )
    return response.data
  } catch (error) {
    throw new Error(handleAIError(error))
  }
}

// ============================================================================
// Health Check
// ============================================================================

export async function checkAIHealth(): Promise<AIHealthResponse> {
  try {
    const response = await aiClient.health.check()
    return {
      status: 'healthy',
      available: response.status === 200,
    }
  } catch (error) {
    return {
      status: 'unhealthy',
      available: false,
    }
  }
}

// ============================================================================
// Exports Helper
// ============================================================================

export { handleAIError }
